import {
  Button,
  Chip,
  ClickAwayListener,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Theme,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import FilterListIcon from '@material-ui/icons/FilterList';
import NotificationImportantIcon from '@material-ui/icons/NotificationImportant';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import { useStores } from 'doc-mate-store/lib/hooks';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';

const TITLE_OPTIONS = [
  'Additional Billing Flagged',
  'Billing Flagged',
  'Load Damages',
  'Driver Rejected',
  'Unassign Error',
  'New Driver - Truck Needed',
  'Unassigned Driver - Truck Needed',
  'Reinstated Driver - Truck Needed',
] as const;

const TERMINAL_OPTIONS = [
  'Melvindale',
  'East Chicago',
  'National City',
] as const;

export const NotificationMenu: React.FC = observer(() => {
  const classes = useStyles();
  const { rootStore } = useStores();

  const [showNotifications, setShowNotifications] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Filter States
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState<string>('');
  const [selectedTerminal, setSelectedTerminal] = useState<string>('');
  const [searchMessage, setSearchMessage] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  // Pagination States
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Search Input Debouncer (400ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchMessage);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchMessage]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [selectedTitle, selectedTerminal, debouncedSearch]);

  const fetchFilteredNotifications = useCallback(async () => {
    const res = await rootStore.fetchNotifications({
      title: selectedTitle || undefined,
      terminal: selectedTerminal,
      message__icontains: debouncedSearch || undefined,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    });

    if (res && typeof res.count === 'number') {
      setTotalCount(res.count);
    }
    await rootStore.fetchUnreadNotificationCount();
  }, [
    rootStore,
    selectedTitle,
    selectedTerminal,
    debouncedSearch,
    page,
    rowsPerPage,
  ]);

  useEffect(() => {
    if (showNotifications && !dialogOpen) {
      rootStore.fetchNotifications({ limit: 10, offset: 0 });
      rootStore.fetchUnreadNotificationCount();
    }
  }, [rootStore, showNotifications, dialogOpen]);

  useEffect(() => {
    if (dialogOpen) {
      fetchFilteredNotifications();
    }
  }, [dialogOpen, fetchFilteredNotifications]);

  const handleNotificationClick = useCallback(() => {
    setShowNotifications(prev => !prev);
  }, []);

  const handleToggleFilter = useCallback(() => {
    setFilterOpen(prev => !prev);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedTitle('');
    setSelectedTerminal('');
    setSearchMessage('');
    setDebouncedSearch('');
    setPage(0);
  }, []);

  const handleMarkAllAsRead = useCallback(async () => {
    await rootStore.bulkNotificationRead();
    if (dialogOpen) {
      await fetchFilteredNotifications();
    } else {
      await rootStore.fetchNotifications({ limit: 10, offset: 0 });
    }
  }, [rootStore, dialogOpen, fetchFilteredNotifications]);

  const handleToggleSingleRead = useCallback(
    async (id: number, isRead: boolean) => {
      await rootStore.updateNotificationRead(id, isRead);
    },
    [rootStore],
  );

  const handleOpenDialog = useCallback(() => {
    setShowNotifications(false);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setFilterOpen(false);
    setSelectedTitle('');
    setSelectedTerminal('');
    setSearchMessage('');
    setDebouncedSearch('');
    setPage(0);
  }, []);

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  const notificationsList = Array.from(rootStore.notifications.values()).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const unreadNotifications = notificationsList.filter(n => !n.isRead);
  const isFiltering = Boolean(
    selectedTitle || selectedTerminal || debouncedSearch,
  );

  return (
    <ClickAwayListener onClickAway={() => setShowNotifications(false)}>
      <div className={classes.notificationContainer}>
        <IconButton
          onClick={handleNotificationClick}
          size="small"
          className={classes.iconBtn}
        >
          {rootStore.unreadNotificationCount > 0 ? (
            <NotificationImportantIcon color="error" />
          ) : (
            <NotificationsNoneIcon color="action" />
          )}
        </IconButton>

        {/* Quick Dropdown Menu */}
        {showNotifications && (
          <div className={classes.dropdown}>
            <div className={classes.dropdownHeader}>
              <Typography variant="subtitle2" color="textSecondary">
                Unread Notifications
              </Typography>
            </div>

            <Divider style={{ marginBottom: 8 }} />

            {unreadNotifications.length > 0 ? (
              unreadNotifications.map(notification => (
                <div key={notification.id} className={classes.notificationItem}>
                  <div className={classes.itemHeader}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      {notification.title || 'Notification'}
                    </Typography>
                    <Button
                      size="small"
                      color="primary"
                      onClick={() =>
                        handleToggleSingleRead(notification.id, true)
                      }
                    >
                      Mark as Read
                    </Button>
                  </div>

                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    style={{ marginBottom: 4 }}
                  >
                    {`${new Date(
                      notification.createdAt,
                    ).toLocaleDateString()} - ${new Date(
                      notification.createdAt,
                    ).toLocaleTimeString()}`}
                  </Typography>

                  <Typography
                    variant="body2"
                    component="div"
                    className={classes.htmlContent}
                    dangerouslySetInnerHTML={{
                      __html: notification.message,
                    }}
                  />

                  <Divider style={{ marginTop: 8, marginBottom: 8 }} />
                </div>
              ))
            ) : (
              <Typography
                align="center"
                variant="body2"
                color="textSecondary"
                style={{ padding: '12px 0' }}
              >
                No unread notifications
              </Typography>
            )}

            <Divider style={{ margin: '8px 0' }} />

            <div className={classes.actionsContainer}>
              <Button
                size="small"
                color="primary"
                onClick={handleMarkAllAsRead}
                disabled={unreadNotifications.length === 0}
              >
                Mark All as Read
              </Button>

              <Button size="small" color="secondary" onClick={handleOpenDialog}>
                Show All Notifications
              </Button>
            </div>
          </div>
        )}

        {/* Fixed Large Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          classes={{ paper: classes.dialogPaper }}
        >
          <DialogTitle disableTypography className={classes.dialogTitle}>
            <Typography variant="h6">All Notifications</Typography>
            <div className={classes.dialogHeaderActions}>
              <Tooltip title={filterOpen ? 'Hide Filters' : 'Show Filters'}>
                <IconButton
                  size="small"
                  onClick={handleToggleFilter}
                  color={filterOpen || isFiltering ? 'primary' : 'default'}
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={handleCloseDialog}>
                <CloseIcon />
              </IconButton>
            </div>
          </DialogTitle>

          <DialogContent dividers className={classes.dialogContent}>
            {/* Collapsible Filter Panel */}
            <Collapse in={filterOpen}>
              <div className={classes.filterPanel}>
                <FormControl
                  variant="outlined"
                  size="small"
                  fullWidth
                  className={classes.filterInput}
                >
                  <InputLabel id="dialog-notification-title-label">
                    Title
                  </InputLabel>
                  <Select
                    labelId="dialog-notification-title-label"
                    value={selectedTitle}
                    onChange={e => setSelectedTitle(e.target.value as string)}
                    label="Title"
                  >
                    <MenuItem value="">
                      <em>All Titles</em>
                    </MenuItem>
                    {TITLE_OPTIONS.map(opt => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  variant="outlined"
                  size="small"
                  fullWidth
                  className={classes.filterInput}
                >
                  <InputLabel id="dialog-notification-terminal-label">
                    Terminal
                  </InputLabel>
                  <Select
                    labelId="dialog-notification-terminal-label"
                    value={selectedTerminal}
                    onChange={e =>
                      setSelectedTerminal(e.target.value as string)
                    }
                    label="Terminal"
                  >
                    <MenuItem value="">
                      <em>All Terminals</em>
                    </MenuItem>
                    {TERMINAL_OPTIONS.map(opt => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Search Message"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={searchMessage}
                  onChange={e => setSearchMessage(e.target.value)}
                  className={classes.filterInput}
                />

                {isFiltering && (
                  <Button
                    size="small"
                    onClick={handleClearFilters}
                    className={classes.clearBtn}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
              <Divider style={{ margin: '12px 0' }} />
            </Collapse>

            {/* Notification List */}
            {notificationsList.length > 0 ? (
              <List disablePadding>
                {notificationsList.map(notification => (
                  <React.Fragment key={notification.id}>
                    <ListItem
                      alignItems="flex-start"
                      className={classes.dialogListItem}
                    >
                      <ListItemText
                        primary={
                          <div className={classes.dialogItemHeader}>
                            <Typography variant="subtitle2">
                              {notification.title || 'Notification'}
                            </Typography>
                            <Chip
                              label={notification.isRead ? 'Read' : 'Unread'}
                              size="small"
                              color={
                                notification.isRead ? 'default' : 'secondary'
                              }
                              variant={
                                notification.isRead ? 'outlined' : 'default'
                              }
                            />
                          </div>
                        }
                        secondary={
                          <>
                            <Typography
                              component="span"
                              variant="body2"
                              color="textPrimary"
                              className={classes.htmlContent}
                              dangerouslySetInnerHTML={{
                                __html: notification.message,
                              }}
                            />
                            <br />
                            <Typography
                              component="span"
                              variant="caption"
                              color="textSecondary"
                            >
                              {`${new Date(
                                notification.createdAt,
                              ).toLocaleDateString()} ${new Date(
                                notification.createdAt,
                              ).toLocaleTimeString()}`}
                            </Typography>
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Button
                          size="small"
                          color={notification.isRead ? 'default' : 'primary'}
                          onClick={() =>
                            handleToggleSingleRead(
                              notification.id,
                              !notification.isRead,
                            )
                          }
                        >
                          {notification.isRead
                            ? 'Mark as Unread'
                            : 'Mark as Read'}
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography
                align="center"
                color="textSecondary"
                style={{ padding: 32 }}
              >
                {isFiltering
                  ? 'No notifications match your filter.'
                  : 'No notifications found.'}
              </Typography>
            )}
          </DialogContent>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />

          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ClickAwayListener>
  );
});

const useStyles = makeStyles((theme: Theme) => ({
  notificationContainer: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconBtn: {
    padding: theme.spacing(1),
  },
  dropdown: {
    width: 340,
    maxHeight: 400,
    overflowY: 'auto',
    position: 'absolute',
    top: 40,
    right: 0,
    zIndex: 100,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[4],
  },
  dropdownHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(0.5),
  },
  dialogPaper: {
    height: '85vh',
    maxHeight: '85vh',
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  dialogHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  dialogContent: {
    flex: 1,
    overflowY: 'auto',
  },
  filterPanel: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0, 0.5, 0),
  },
  filterInput: {
    flex: 1,
  },
  clearBtn: {
    whiteSpace: 'nowrap',
  },
  notificationItem: {
    padding: theme.spacing(0.5, 0),
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  dialogListItem: {
    paddingRight: theme.spacing(18),
  },
  dialogItemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  htmlContent: {
    display: 'block',
    '& p': {
      margin: 0,
    },
    '& a': {
      color: theme.palette.primary.main,
    },
  },
}));

export default NotificationMenu;
